import React, { useState, useEffect } from "react";
import { FaFolder, FaFile, FaTimes, FaArrowLeft } from "react-icons/fa";
import { showErrorToast, showSuccessToast } from "../Toast";

const OneDriveFilePicker = ({
  isOpen,
  onClose,
  accessToken,
  onFileSelect,
  onFolderSelect,
  folderMode = false,
}) => {
  const [currentPath, setCurrentPath] = useState({
    id: "root",
    name: "My Files",
  });
  const [pathHistory, setPathHistory] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const allowedExtensions = [".doc", ".docx", ".pdf", ".jpg", ".jpeg", ".png"];

  // Helper function to get download URL when missing
  // Returns the /content endpoint URL directly - backend will handle the redirect
  const getDownloadUrl = (fileId) => {
    // Return the /content endpoint URL directly
    // The backend can follow the redirect to get the actual download URL
    // This avoids making unnecessary HEAD requests
    return `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`;
  };

  useEffect(() => {
    if (isOpen && accessToken) {
      fetchItems(currentPath.id);
    }
  }, [isOpen, currentPath.id, accessToken]);

  const fetchItems = async (folderId) => {
    setLoading(true);
    try {
      const endpoint =
        folderId === "root"
          ? "https://graph.microsoft.com/v1.0/me/drive/root/children"
          : `https://graph.microsoft.com/v1.0/me/drive/items/${folderId}/children`;

      const response = await fetch(
        `${endpoint}?$select=id,name,size,folder,file,@microsoft.graph.downloadUrl`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch items: ${response.status}`);
      }

      const data = await response.json();
      setItems(data.value || []);
    } catch (error) {
      console.error("Error fetching OneDrive items:", error);
      showErrorToast("Failed to fetch OneDrive items. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    // If in folder mode, select the folder instead of navigating
    if (folderMode && onFolderSelect) {
      // Fetch full folder details including webUrl
      fetch(
        `https://graph.microsoft.com/v1.0/me/drive/items/${folder.id}?$select=id,name,webUrl`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      )
        .then((res) => res.json())
        .then((folderDetails) => {
          const activeChatbotId = localStorage.getItem("active_chatbot_id");
          if (activeChatbotId) {
            folderDetails.active_chatbot_id = activeChatbotId;
          }
          onFolderSelect(folderDetails);
          onClose();
        })
        .catch((error) => {
          console.error("Error fetching folder details:", error);
          // Fallback to using basic folder info
          const activeChatbotId = localStorage.getItem("active_chatbot_id");
          if (activeChatbotId) {
            folder.active_chatbot_id = activeChatbotId;
          }
          onFolderSelect(folder);
          onClose();
        });
      return;
    }

    // Normal navigation mode
    setPathHistory([...pathHistory, currentPath]);
    setCurrentPath({ id: folder.id, name: folder.name });
  };

  const handleBackClick = () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1];
      setCurrentPath(previousPath);
      setPathHistory(pathHistory.slice(0, -1));
    }
  };

  const handleFileClick = async (file) => {
    const fileName = file.name.toLowerCase();
    const isAllowed = allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!isAllowed) {
      showErrorToast(
        "File type not supported. Please select DOC, DOCX, PDF, JPG, PNG, or TXT files only."
      );
      return;
    }

    // Fetch the complete file details from Microsoft Graph API
    // Including all fields needed for backend: id, name, size, file, downloadUrl, webUrl
    try {
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/drive/items/${file.id}?$select=id,name,size,file,@microsoft.graph.downloadUrl,webUrl`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch file details");
      }

      const fileDetails = await response.json();

      // If downloadUrl is missing, use the /content endpoint URL directly
      // Backend will handle the redirect
      if (!fileDetails["@microsoft.graph.downloadUrl"]) {
        console.log("Download URL missing, using /content endpoint URL");
        fileDetails["@microsoft.graph.downloadUrl"] = getDownloadUrl(
          fileDetails.id
        );
        console.log(
          "Using /content endpoint URL (backend will handle redirect)"
        );
      }

      console.log("=== ONEDRIVE FILE SELECTED (PICKER) ===");
      console.log("📦 File Structure:", {
        hasODataContext: !!fileDetails["@odata.context"],
        hasODataEtag: !!fileDetails["@odata.etag"],
        id: fileDetails.id,
        name: fileDetails.name,
        size: fileDetails.size,
        hasWebUrl: !!fileDetails.webUrl,
        mimeType: fileDetails.file?.mimeType,
        hasDownloadUrl: !!fileDetails["@microsoft.graph.downloadUrl"],
      });
      console.log("File ID:", fileDetails.id);
      console.log("File Name:", fileDetails.name);
      console.log(
        "File Size:",
        fileDetails.size
          ? `${(fileDetails.size / 1024 / 1024).toFixed(2)} MB`
          : "Unknown"
      );
      console.log("Web URL:", fileDetails.webUrl);
      console.log("Download URL:", fileDetails["@microsoft.graph.downloadUrl"]);
      console.log("=== END FILE DETAILS ===");

      const activeChatbotId = localStorage.getItem("active_chatbot_id");
      if (activeChatbotId) {
        fileDetails.active_chatbot_id = activeChatbotId;
      }

      showSuccessToast(`File selected: ${fileDetails.name}`);
      onFileSelect(fileDetails);
      onClose();
    } catch (error) {
      console.error("Error fetching file details:", error);
      showErrorToast("Failed to get file download URL");
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split(".").pop();
    const colors = {
      doc: "text-blue-600",
      docx: "text-blue-600",
      pdf: "text-red-600",
      xlsx: "text-green-600",
      xls: "text-green-600",
      pptx: "text-orange-600",
    };
    return colors[ext] || "text-gray-600";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              {folderMode
                ? "Select OneDrive Folder (Auto Ingestion)"
                : "Select from OneDrive"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Breadcrumb / Path */}
        <div className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          {pathHistory.length > 0 && (
            <button
              onClick={handleBackClick}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
            >
              <FaArrowLeft className="w-3 h-3" />
              Back
            </button>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <FaFolder className="w-4 h-4 text-blue-500" />
            <span className="font-medium">{currentPath.name}</span>
          </div>
        </div>

        {/* File/Folder List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <FaFolder className="w-16 h-16 mb-2 opacity-50" />
              <p>This folder is empty</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    if (item.folder) {
                      handleFolderClick(item);
                    } else {
                      handleFileClick(item);
                    }
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${item.file &&
                    !allowedExtensions.some((ext) =>
                      item.name.toLowerCase().endsWith(ext)
                    )
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                    }`}
                >
                  {item.folder ? (
                    <FaFolder className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  ) : (
                    <FaFile
                      className={`w-8 h-8 flex-shrink-0 ${getFileIcon(
                        item.name
                      )}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-white truncate">
                      {item.name}
                    </p>
                    {item.file && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {item.size
                          ? `${(item.size / 1024 / 1024).toFixed(2)} MB`
                          : "Unknown size"}
                      </p>
                    )}
                    {item.folder && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Folder
                      </p>
                    )}
                  </div>
                  {item.file &&
                    !allowedExtensions.some((ext) =>
                      item.name.toLowerCase().endsWith(ext)
                    ) && (
                      <span className="text-xs text-red-500 dark:text-red-400">
                        Not supported
                      </span>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {folderMode
              ? "Select a folder to enable automated ingestion of files from this folder"
              : "Supported file types: DOC, DOCX, PDF, TXT, JPG, PNG"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OneDriveFilePicker;
